import React from 'react';
import type { Point } from '../../../context/context';
import type { TransitStopServiceSummary } from '../../../utils/transitStopService';

interface TransitStopPopupContentProps {
  pointName: string;
  coordinates?: [number, number];
  services: TransitStopServiceSummary[];
  emptyMessage: string;
  description?: string;
}

const mutedText: React.CSSProperties = {
  color: '#666',
  fontSize: 12,
};

const TransitStopPopupContent: React.FC<TransitStopPopupContentProps> = ({
  pointName,
  coordinates,
  services,
  emptyMessage,
  description,
}) => {
  return (
    <div style={{ minWidth: 260, maxWidth: 340, fontFamily: 'inherit' }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
        {pointName || 'Unnamed stop'}
      </div>
      {description && (
        <div style={{ ...mutedText, marginBottom: 8 }}>
          {description}
        </div>
      )}
      {coordinates && (
        <div style={{ ...mutedText, marginBottom: 8 }}>
          <strong>Coordinates:</strong> {coordinates[0].toFixed(6)}, {coordinates[1].toFixed(6)}
        </div>
      )}

      <div style={{ borderTop: '1px solid #ddd', paddingTop: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>
          Routes and timetables
        </div>
        {services.length === 0 ? (
          <div style={mutedText}>{emptyMessage}</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {services.map((service) => (
              <div key={`${service.source}-${service.routeId}`} style={{ display: 'grid', gap: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {service.colour && (
                    <span
                      aria-hidden
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 999,
                        background: service.colour,
                        border: '1px solid rgba(0,0,0,0.15)',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <strong style={{ fontSize: 12 }}>{service.routeLabel}</strong>
                </div>
                {(service.modeLabel || service.operatorLabel || service.directionLabel) && (
                  <div style={{ ...mutedText, fontSize: 11 }}>
                    {[service.modeLabel, service.operatorLabel, service.directionLabel].filter(Boolean).join(' - ')}
                  </div>
                )}
                <div style={{ ...mutedText, color: '#444' }}>
                  {service.timetableLabel}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export function TransitOsmStopPopupContent({
  point,
  services,
}: {
  point: Point;
  services: TransitStopServiceSummary[];
}) {
  return (
    <TransitStopPopupContent
      pointName={point.name}
      coordinates={point.coordinates}
      description={point.description}
      services={services}
      emptyMessage="No route metadata is linked to this stop yet."
    />
  );
}

export function FixedRouteStopPopupContent({
  stopName,
  coordinates,
  address,
  description,
  services,
}: {
  stopName: string;
  coordinates: [number, number];
  address?: string;
  description?: string;
  services: TransitStopServiceSummary[];
}) {
  return (
    <TransitStopPopupContent
      pointName={stopName}
      coordinates={coordinates}
      description={[address ? `Address: ${address}` : null, description].filter(Boolean).join(' - ')}
      services={services}
      emptyMessage="This stop is not assigned to any visible route."
    />
  );
}

export default TransitStopPopupContent;
